/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE.
*/

import { CosmosTable, CosmosTagProps, CosmosText } from "@components/elements/atoms";
import { useOsName } from "@components/hooks";
import { PluginListItemFragment } from "@cosmos/hygraph-schema";
import { useMessages } from "@epic-mw/i18n";
import { css } from "@panda/css";
import { VStack } from "@panda/jsx";
import { useCallback, useEffect, useMemo, useState } from "react";
import { PluginListTableItem } from "./PluginListTable.item";

export interface PluginListTableProps {
	items?: PluginListItemFragment[];
	osName?: string;
	onOsNameChange?: (os: string) => void;
}

interface ProcessedItem extends PluginListItemFragment {
	availableOses: Set<string>;
	hasUpdatedDate: boolean;
	tags: CosmosTagProps[];
}

export const PluginListTable = ({
	items = [],
	osName: providedOsName,
	onOsNameChange,
}: PluginListTableProps): React.JSX.Element | null => {
	const messages = useMessages();
	const detectedOsName = useOsName();

	const [osName, setOsName] = useState(providedOsName);
	const [prestine, setPrestine] = useState(true);

	const handleOsChange = useCallback(
		(newOs: string) => {
			if (onOsNameChange) onOsNameChange(newOs);
			setOsName(newOs);
		},
		[onOsNameChange],
	);

	// Pre-process items: extract OSes, date flags, and compatibility tags at top level
	const { processedItems, allOses } = useMemo(() => {
		const processedItems: ProcessedItem[] = [];
		const allOses = new Set<string>();

		for (const item of items) {
			const availableOses = new Set<string>();
			let hasUpdatedDate = true;
			const tags: CosmosTagProps[] = item?.compatibilityTags?.map((label) => ({ label })) || [];

			if (item.downloads) {
				for (const download of item.downloads) {
					if (download.operatingSystem) {
						const os = download.operatingSystem;
						availableOses.add(os.toLocaleLowerCase());
						allOses.add(os);
					}
				}
			}

			const newItem: ProcessedItem = {
				...item,
				availableOses,
				hasUpdatedDate,
				tags,
			};

			processedItems.push(newItem);
		}

		return { processedItems, allOses };
	}, [items]);

	useEffect(() => {
		if (prestine && detectedOsName) {
			setPrestine(false);
			if (allOses.has(detectedOsName)) {
				if (detectedOsName !== osName) handleOsChange(detectedOsName);
			} else if ("windows" !== osName) handleOsChange("windows");
		} else if (typeof providedOsName === "string" && providedOsName !== osName) {
			handleOsChange(providedOsName);
		}
	}, [osName, handleOsChange, providedOsName, prestine, detectedOsName, allOses]);

	// Check if any filtered items have dates - simple top-level check
	const { hasUpdatedDate, hasSize } = useMemo(() => {
		let hasUpdatedDate = false;
		let hasSize = false;

		for (const item of processedItems) {
			if (item.hasUpdatedDate) hasUpdatedDate = true;
			if (item.downloads.some((el) => el?.sizeInBytes && el?.sizeInBytes > 0)) hasSize = true;
			// Early exit if we found both
			if (hasUpdatedDate && hasSize) break;
		}

		return { hasUpdatedDate, hasSize };
	}, [processedItems]);

	if (!items?.length) {
		return null;
	}

	// Build dynamic grid template based on which date columns are present
	//const dateColumnsCount = (hasUploadedDates ? 1 : 0) + (hasUpdatedDates ? 1 : 0);
	const gridTemplate = `minmax(300px, 1fr) max-content max-content max-content max-content`;

	return (
		<VStack gap="eds.dimension.24" alignItems="initial" colorScheme="dark">
			<CosmosTable gridTemplateColumns={gridTemplate}>
				<CosmosTable.Header>
					<CosmosTable.Column>
						<CosmosText
							variant="uiMD"
							color="eds.text.secondary"
							text={messages["epic.cosmos.blob.filename"] || "File name"}
						/>
					</CosmosTable.Column>
					{hasSize ? (
						<CosmosTable.Column>
							<CosmosText
								variant="uiMD"
								color="eds.text.secondary"
								text={messages["epic.cosmos.blob.size"] || "Size"}
							/>
						</CosmosTable.Column>
					) : null}
					{hasUpdatedDate ? (
						<CosmosTable.Column>
							<CosmosText
								variant="uiMD"
								color="eds.text.secondary"
								text={messages["epic.cosmos.blob.dateLastUpdated"] || "Last updated"}
							/>
						</CosmosTable.Column>
					) : null}
					<CosmosTable.Column>
						<CosmosText variant="uiMD" color="eds.text.secondary" text="" />
					</CosmosTable.Column>
				</CosmosTable.Header>
				<CosmosTable.Body w="full" className={css({ "--table-border-radius": "radii.eds.borderRadius.24" })}>
					{processedItems.map((item) => (
						<PluginListTableItem
							key={item.id}
							{...item}
							showUpdatedDate={hasUpdatedDate}
							showSize={hasSize}
							osName={osName}
						/>
					))}
				</CosmosTable.Body>
			</CosmosTable>
		</VStack>
	);
};
